<template>
  <div :id="'at-' + id">
    <h2 v-if="!!persistedRecord" class="text-center assignment-term-heading">
      #{{ this.id }} {{ this.name }}
    </h2>
    <b-card no-body>
      <b-tabs>
        <b-tab no-body title="Current info" active>
          <b-form
            @submit.prevent="submitForm"
            v-promise-btn="{ action: 'submit' }"
          >
            <div class="card-header" v-bind:class="statusCssClass">
              <b-row>
                <b-col>
                  <span class="text-secondary align-middle"> # {{ id }} </span>
                  <span class="align-middle"
                    ><b>({{ status }})</b></span
                  >
                </b-col>
              </b-row>
            </div>
            <div class="card-body">
              <b-container fluid>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="handleClickOnDigitalMatching()"
                >
                  <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                  Digital Matching
                </b-button>
                <div class="mb-3" v-if="errors.length">
                  <ErrorsContainer :errors="errors" />
                </div>

                <b-alert
                  variant="success"
                  dismissible
                  fade
                  :show="showSuccessUpdateMessage"
                >
                  Assignment term was successfully updated.
                </b-alert>
                <div v-if="this.testAssignmentTerm">
                  <h2><b-badge pill variant="danger">Test</b-badge></h2>
                </div>
                <b-row class="mt-3">
                  <b-col md="auto">
                    <div v-show="nameEditMode">
                      <input
                        v-model="assignmentTermForm.name"
                        :ref="'nameInput-' + id"
                        @blur="toggleNameEdit(false, id);"
                      />
                    </div>
                    <div v-show="!nameEditMode">
                      <h4
                        v-if="assignmentTermForm.name"
                        @click="toggleNameEdit(true, id);"
                      >
                        {{ assignmentTermForm.name }}
                      </h4>
                      <span
                        v-else
                        class="text-secondary"
                        @click="toggleNameEdit(true, id);"
                      >
                        Click here to set name
                      </span>
                    </div>
                  </b-col>
                </b-row>

                <b-row class="mt-3">
                  <b-col md="auto"> <h4>Contingents</h4> </b-col>
                </b-row>
                <b-row>
                  <b-col md="auto">
                    <b-form-group label="Daily:" label-for="dailyContingent">
                      <input
                        type="number"
                        v-model="assignmentTermForm.contingentForm.daily"
                        placeholder="∞"
                        :readonly="updateForm"
                        :disabled="updateForm"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col md="auto">
                    <b-form-group label="Weekly:" label-for="weeklyContingent">
                      <input
                        type="number"
                        v-model="assignmentTermForm.contingentForm.weekly"
                        placeholder="∞"
                        :readonly="updateForm"
                        :disabled="updateForm"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col md="auto">
                    <b-form-group
                      label="Monthly:"
                      label-for="monthlyContingent"
                    >
                      <input
                        type="number"
                        v-model="assignmentTermForm.contingentForm.monthly"
                        placeholder="∞"
                        :readonly="updateForm"
                        :disabled="updateForm"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row v-if="!!persistedRecord">
                  <b-col md="auto">
                    <b-collapse
                      id="collapse-daily-remain"
                      :visible="contingentRemainsDisplay.daily"
                    >
                      <b-form-group label="Daily remain:">
                        <b-input-group>
                          <input
                            type="number"
                            v-model.number="
                              assignmentTermForm.contingentForm.dailyRemain
                            "
                            readonly
                            disabled
                          />
                          <b-input-group-append v-if="!updateForm">
                            <b-btn
                              @click="switchRemainDiffToDisplay('daily');"
                              variant="secondary"
                              size="sm"
                            >
                              <font-awesome-icon
                                icon="edit"
                              ></font-awesome-icon>
                            </b-btn>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-collapse>

                    <b-collapse
                      id="collapse-daily-remain-diff"
                      v-if="!updateForm"
                      :visible="!contingentRemainsDisplay.daily"
                    >
                      <b-form-group label="Daily remain diff:">
                        <b-input-group>
                          <input
                            type="number"
                            v-model.number="dailyRemainDiff"
                            v-on:blur="switchRemainDiffToDisplay('daily');"
                          />
                          <b-input-group-append>
                            <b-btn
                              @click="switchRemainDiffToDisplay('daily');"
                              variant="secondary"
                              size="sm"
                            >
                              <font-awesome-icon
                                icon="edit"
                              ></font-awesome-icon>
                            </b-btn>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-collapse>
                  </b-col>

                  <b-col md="auto">
                    <b-collapse
                      id="collapse-weekly-remain"
                      :visible="contingentRemainsDisplay.weekly"
                    >
                      <b-form-group label="Weekly remain:">
                        <b-input-group>
                          <input
                            type="number"
                            v-model.number="
                              assignmentTermForm.contingentForm.weeklyRemain
                            "
                            readonly
                            disabled
                          />
                          <b-input-group-append v-if="!updateForm">
                            <b-btn
                              @click="switchRemainDiffToDisplay('weekly');"
                              variant="secondary"
                              size="sm"
                            >
                              <font-awesome-icon
                                icon="edit"
                              ></font-awesome-icon>
                            </b-btn>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-collapse>

                    <b-collapse
                      id="collapse-weekly-remain-diff"
                      v-if="!updateForm"
                      :visible="!contingentRemainsDisplay.weekly"
                    >
                      <b-form-group label="Weekly remain diff:">
                        <b-input-group>
                          <input
                            type="number"
                            v-model.number="weeklyRemainDiff"
                            v-on:blur="switchRemainDiffToDisplay('weekly');"
                          />
                          <b-input-group-append>
                            <b-btn
                              @click="switchRemainDiffToDisplay('weekly');"
                              variant="secondary"
                              size="sm"
                            >
                              <font-awesome-icon
                                icon="edit"
                              ></font-awesome-icon>
                            </b-btn>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-collapse>
                  </b-col>

                  <b-col md="auto">
                    <b-collapse
                      id="collapse-monthly-remain"
                      :visible="contingentRemainsDisplay.monthly"
                    >
                      <b-form-group label="Monthly remain:">
                        <b-input-group>
                          <input
                            type="number"
                            v-model.number="
                              assignmentTermForm.contingentForm.monthlyRemain
                            "
                            readonly
                            disabled
                          />
                          <b-input-group-append v-if="!updateForm">
                            <b-btn
                              @click="switchRemainDiffToDisplay('monthly');"
                              variant="secondary"
                              size="sm"
                            >
                              <font-awesome-icon
                                icon="edit"
                              ></font-awesome-icon>
                            </b-btn>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-collapse>

                    <b-collapse
                      id="collapse-monthly-remain-diff"
                      v-if="!updateForm"
                      :visible="!contingentRemainsDisplay.monthly"
                    >
                      <b-form-group label="Monthly remain diff:">
                        <b-input-group>
                          <input
                            type="number"
                            v-model.number="monthlyRemainDiff"
                            v-on:blur="switchRemainDiffToDisplay('monthly');"
                          />
                          <b-input-group-append>
                            <b-btn
                              @click="switchRemainDiffToDisplay('monthly');"
                              variant="secondary"
                              size="sm"
                            >
                              <font-awesome-icon
                                icon="edit"
                              ></font-awesome-icon>
                            </b-btn>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-collapse>
                  </b-col>

                  <b-col md="auto">
                    <b-collapse
                      id="collapse-unique-remain"
                      :visible="contingentRemainsDisplay.unique"
                    >
                      <b-form-group label="Unique remain:">
                        <b-input-group>
                          <input
                            type="number"
                            v-model.number="
                              assignmentTermForm.contingentForm.uniqueRemain
                            "
                            readonly
                            disabled
                          />
                          <b-input-group-append v-if="!updateForm">
                            <b-btn
                              @click="switchRemainDiffToDisplay('unique');"
                              variant="secondary"
                              size="sm"
                            >
                              <font-awesome-icon
                                icon="edit"
                              ></font-awesome-icon>
                            </b-btn>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-collapse>

                    <b-collapse
                      id="collapse-unique-remain-diff"
                      v-if="!updateForm"
                      :visible="!contingentRemainsDisplay.unique"
                    >
                      <b-form-group label="Unique remain diff:">
                        <b-input-group>
                          <input
                            type="number"
                            v-model.number="uniqueRemainDiff"
                            v-on:blur="switchRemainDiffToDisplay('unique');"
                          />
                          <b-input-group-append>
                            <b-btn
                              @click="removeUniqueRemain"
                              variant="danger"
                              size="sm"
                              title="Remove unique remain"
                            >
                              <font-awesome-icon
                                icon="trash"
                              ></font-awesome-icon>
                            </b-btn>
                            <b-btn
                              @click="switchRemainDiffToDisplay('unique');"
                              variant="secondary"
                              size="sm"
                            >
                              <font-awesome-icon
                                icon="edit"
                              ></font-awesome-icon>
                            </b-btn>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-collapse>
                  </b-col>
                </b-row>
                <hr />

                <div v-if="!persistedRecord">
                  <h4>Delivery</h4>
                  <b-row>
                    <b-col md="auto">
                      <b-form-group
                        label="Delivery starts at:"
                        label-for="deliveryStartAt"
                      >
                        <date-picker
                          v-model="assignmentTermForm.deliveryStartAt"
                          :first-day-of-week="datepickerOptions.firstDayOfWeek"
                          :clearable="datepickerOptions.clearable"
                          :editable="datepickerOptions.editable"
                          :lang="datepickerOptions.lang"
                          :format="datepickerOptions.format"
                          :not-before="datepickerOptions.notBefore"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <hr />
                </div>

                <h4>Lead details</h4>

                <b-row>
                  <b-col md="auto" class="mt-4 pt-2" v-if="!persistedRecord">
                    <b-form-group>
                      <div
                        class="btn-group btn-group-toggle"
                        data-toggle="buttons"
                      >
                        <b-form-radio-group
                          buttons
                          button-variant="outline-primary"
                          size="sm"
                          v-model="assignmentTermForm.buysDates"
                          :options="buysDatesOptions"
                          name="buysDates"
                        >
                        </b-form-radio-group>
                      </div>
                    </b-form-group>
                  </b-col>
                  <b-col md="auto" class="mt-4 pt-2" v-if="!!persistedRecord">
                    <p class="marketplace-or-instant-buy">
                      <span class="badge badge-primary">
                        <h6 class="mb-0">
                          {{ this.instant_buy ? "Instant Buy" : "Marketplace" }}
                        </h6>
                      </span>
                    </p>
                  </b-col>
                  <b-col md="auto" class="mt-4 pt-2" v-if="!!persistedRecord">
                    <p>
                      <span class="badge badge-primary">
                        <h6 class="mb-0">{{ buysDatesLabel }}</h6>
                      </span>
                    </p>
                  </b-col>
                  <b-col md="auto" class="mt-4 pt-2" v-if="assignmentTermForm.buysDates && !!persistedRecord">
                    <p>
                      <span class="badge badge-primary">
                        <h6 class="mb-0">{{ buysDatesTypeLabel }}</h6>
                      </span>
                    </p>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col
                    md="auto"
                    class="mt-4 pt-2"
                    v-if="assignmentTermForm.buysDates && isNet"
                  >
                    <b-form-group
                      label="Feedback time (in hours):"
                      label-for="feedbackTime"
                      :state="isValidFeedBackTime()"
                      :invalid-feedback="feedbackTimeError"
                    >
                      <b-form-input
                        id="feedbackTime"
                        type="number"
                        size="sm"
                        :state="isValidFeedBackTime()"
                        v-model="assignmentTermForm.feedbackTime"
                      >
                      </b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="auto" class="mt-4 pt-2">
                    <label for="country">Select Pricing Setting:</label>
                    <b-form-select
                      class="mb-3"
                      v-model="assignmentTermForm.pricingSetting"
                    >
                      <option
                        :value="setting.key"
                        v-for="setting in pricingSettings"
                        :key="setting.key"
                        >{{ setting.text }}</option
                      >
                    </b-form-select>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="auto" v-if="!dynamicLeadPricing">
                    <b-form-group
                      :label="oneSellerPriceLabel"
                      label-for="onePriceInput"
                      :state="isValidPricing('oneSellerPrice')"
                      :invalid-feedback="priceErrorMessages.oneSellerPrice"
                    >
                      <b-form-input
                        id="onePriceInput"
                        size="sm"
                        :state="isValidPricing('oneSellerPrice')"
                        v-model="assignmentTermForm.oneSellerPrice"
                      >
                      </b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="auto" v-if="dynamicLeadPricing">
                    <b-form-group>
                      <p>
                        The actual dynamic leadprice is calculated based on the
                        pricematrix of customer-request-valuation.
                      </p>
                      <b-form-input
                        :disabled="true"
                        size="sm"
                        v-model="assignmentTermForm.dynamicLeadPricingValue"
                      >
                      </b-form-input>
                      <label>
                        The value above is based on a Product Country average lead price configured in Salesforce. <br />
                        If you see an error regarding a Product Country average lead price not found <br />
                        then you need to configure the 'Dynamic' average lead price in Salesforce <br />
                        for the corresponding Product Country Junction object.
                      </label>
                    </b-form-group>
                  </b-col>
                  <b-col md="auto" v-if="larPricing">
                    <b-form-group
                      label="Two Sellers Price:"
                      label-for="twoPriceInput"
                      :invalid-feedback="priceErrorMessages.twoSellersPrice"
                      :state="isValidPricing('twoSellersPrice')"
                    >
                      <b-form-input
                        id="twoPriceInput"
                        size="sm"
                        :state="isValidPricing('twoSellersPrice')"
                        v-model="assignmentTermForm.twoSellersPrice"
                      >
                      </b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="auto" v-if="larPricing">
                    <b-form-group
                      label="Three Sellers Price:"
                      label-for="threePriceInput"
                      :invalid-feedback="priceErrorMessages.threeSellersPrice"
                      :state="isValidPricing('threeSellersPrice')"
                    >
                      <b-form-input
                        id="threePriceInput"
                        size="sm"
                        :state="isValidPricing('threeSellersPrice')"
                        v-model="assignmentTermForm.threeSellersPrice"
                      >
                      </b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="auto">
                    <b-form-group>
                      <b-form-group
                        label="Auto Assignment Ranking:"
                        label-for="autoAssignmentRanking"
                      >
                        <input
                          type="number"
                          v-model="assignmentTermForm.autoAssignmentRanking"
                        />
                      </b-form-group>
                    </b-form-group>
                  </b-col>
                  <b-col md="auto" v-if="!!persistedRecord" class="mt-4 pt-2">
                    <label>Buying Probability: {{ buying_probability }}</label>
                  </b-col>
                  <b-col v-if="!!persistedRecord" md="auto" class="mt-4 pt-2">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :to="{
                        name: 'Options',
                        params: {
                          id: this.id,
                          productId: this.productId,
                          vendorId: this.vendorId
                        }
                      }"
                    >
                      <font-awesome-icon icon="edit" />
                      Change Criteria
                    </b-button>
                  </b-col>
                </b-row>

                <hr />
                <b-row>
                  <b-col md="auto" :id="'currentRadius-' + id">
                    <b-form-group label="Radius:" label-for="currentRadius">
                      <input
                        type="number"
                        v-model="assignmentTermForm.radius"
                        :disabled="updateForm || districtCodesFormDirty"
                      />
                    </b-form-group>
                    <b-popover
                      :target="'currentRadius-' + id"
                      :disabled="updateForm || !districtCodesFormDirty"
                      triggers="hover focus"
                      content="Please specify either radius or country"
                    >
                    </b-popover>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="12">
                    <b-form-group>
                      <b-button
                        class="float-right"
                        type="submit"
                        size="lg"
                        variant="success"
                        :disabled="!canBeSubmited"
                      >
                        <font-awesome-icon icon="save" />
                        {{ submitButtonText }}
                      </b-button>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-container>
              <DistrictCodesBar
                ref="districtCodeForm"
                v-bind:districtCodes="district_codes"
                v-bind:assignmentTermId="id"
                v-bind:radiusWasSet="radiusPresent"
                v-bind:assignmentTermForm="assignmentTermForm"
                v-bind:updateForm="updateForm"
              />
            </div>
          </b-form>
        </b-tab>
        <b-tab
          title="Requested changes"
          @click="handleClickOnRequestedChangesTab();"
          v-if="!!persistedRecord"
        >
          <div class="card-body">
            <p>
              Requested Changes area now managed in the Internal Admin. Click
              <a
                :href="
                  this.linkForAssignmentTermInInternalAdmin('requested-changes')
                "
                target="_blank"
                >here</a
              >
              to manage requested changes for this assignment term.
            </p>
          </div>
        </b-tab>
        <b-tab
          title="Audits"
          @click="handleClickOnAuditsTab();"
          v-if="!!persistedRecord"
        >
          <div class="card-body">
            <p>
              Audits area now managed in the Internal Admin. Click
              <a
                :href="this.linkForAssignmentTermInInternalAdmin('audits')"
                target="_blank"
                >here</a
              >
              to view audits for this assignment term.
            </p>
          </div>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import Vue from 'vue'
import DatePicker from 'vue2-datepicker'
import { numeric, required, between } from 'vuelidate/lib/validators'
import AssignmentTermsProvider from '@/services/AssignmentTermsProvider'
import PricingSettingsProvider from '@/services/PricingSettingsProvider'
import DistrictCodesBar from '@/components/DistrictCodesBar'
import ErrorsContainer from '@/components/ErrorsContainer'
import deepmerge from 'deepmerge'
import _ from 'lodash'

function integerOrFloat (value) {
  if (value) {
    let pattern = /^\s*(?=.*[0-9])\d*(?:\.\d{1,2})?\s*$/g
    return pattern.test(value)
  } else {
    return true
  }
}

function positiveInteger (value) {
  if (value) {
    let pattern = /^[1-9]\d*$/g
    return pattern.test(value)
  } else {
    return true
  }
}

function lessOrEqlSecondPrice (value) {
  if (
    !_.isNaN(+value) &&
    this.assignmentTermForm.twoSellersPrice &&
    !_.isNaN(+this.assignmentTermForm.twoSellersPrice)
  ) {
    return +value <= this.assignmentTermForm.twoSellersPrice
  } else {
    return true
  }
}

function lessOrEqlFirstPrice (value) {
  if (
    !_.isNaN(+value) &&
    this.assignmentTermForm.oneSellerPrice &&
    !_.isNaN(+this.assignmentTermForm.oneSellerPrice)
  ) {
    return +value <= this.assignmentTermForm.oneSellerPrice
  } else {
    return true
  }
}

export default {
  name: 'AssignmentTermItem',
  created () {
    this.fetchAllPriceSettings()
  },
  props: {
    id: Number,
    name: String,
    vendorId: [Number, String],
    vendorUuid: String,
    productId: [Number, String],
    productUuid: String,
    status: String,
    pricing: {
      type: Object,
      default: function () {
        return { setting: 'simple_pricing' }
      }
    },
    lead_price: Number,
    one_seller_price: Number,
    two_sellers_price: Number,
    three_sellers_price: Number,
    test: Boolean,
    contingent: {
      type: Object,
      default: function () {
        return { daily: null, weekly: null, monthly: null }
      }
    },
    district_codes: Array,
    instant_buy: { type: Boolean, default: true },
    buys_dates: { type: Boolean, default: false },
    delivery_start_at: String,
    radius: Number,
    buying_probability: Number,
    auto_assignment_ranking: Number,
    ordered_dates_type: { type: String, default: null },
    feedback_time: Number,
    refreshFunction: {
      type: Function,
      default: () => {}
    },
    /**
     * this flag is used to hide features we implemented on internal-admin and not update here anymore
     * we are using this flag when we clean up feature flag from the places that we bound to.
     */
    updateForm: { type: Boolean, default: false }
  },
  data () {
    return {
      nameEditMode: false,
      assignmentTermForm: this.buildAssignmentTermForm(),
      pricingSettings: {
        selected: this.pricingSetting || 'simple_pricing'
      },
      buysDatesOptions: [
        {
          text: 'Dates',
          value: true
        },
        {
          text: 'Leads',
          value: false
        }
      ],
      grossNetOptions: [
        {
          text: 'Gross',
          value: 'gross'
        },
        {
          text: 'Net',
          value: 'net'
        }
      ],
      datepickerOptions: {
        firstDayOfWeek: Vue.defaultDatepickerOptions.firstDayOfWeek,
        clearable: true,
        lang: Vue.defaultDatepickerOptions.lang,
        format: Vue.defaultDatepickerOptions.format,
        editable: Vue.defaultDatepickerOptions.editable,
        notBefore: Vue.defaultDatepickerOptions.notBefore
      },
      contingentRemainsDisplay: {
        daily: true,
        weekly: true,
        monthly: true,
        unique: true
      },
      errors: [],
      priceErrorMessages: {
        oneSellerPrice: '',
        twoSellersPrice: '',
        threeSellersPrice: ''
      },
      feedbackTimeError: '',
      showSuccessUpdateMessage: false,
      defaultValdationRules: {
        assignmentTermForm: {
          vendorId: { required },
          productId: { required },
          contingentForm: {
            daily: { numeric },
            weekly: { numeric },
            monthly: { numeric }
          },
          radius: { numeric },
          pricingSetting: { required },
          oneSellerPrice: {
            integerOrFloat
          }
        }
      },
      dynamicPricesValidation: {
        assignmentTermForm: {
          twoSellersPrice: {
            integerOrFloat,
            lessOrEqlFirstPrice
          },
          threeSellersPrice: {
            integerOrFloat,
            lessOrEqlSecondPrice
          }
        }
      },
      oneSellerPriceRequired: {
        assignmentTermForm: {
          oneSellerPrice: {
            required
          }
        }
      },
      twoSellersPriceRequired: {
        assignmentTermForm: {
          twoSellersPrice: {
            required
          }
        }
      },
      feedback_time_validation: {
        assignmentTermForm: {
          feedbackTime: {
            required,
            positiveInteger,
            between: between(1, 999)
          }
        }
      }
    }
  },
  components: {
    DatePicker,
    DistrictCodesBar,
    ErrorsContainer
  },
  computed: {
    statusCssClass () {
      switch (this.status) {
        case 'active':
          return 'bg-success'
        case 'paused':
          return 'bg-warning'
        case 'inactive':
          return 'bg-danger'
      }
    },
    isNet () {
      return this.assignmentTermForm.orderedDatesType === 'net'
    },
    oneSellerPriceLabel () {
      switch (this.assignmentTermForm.pricingSetting) {
        case 'simple_pricing':
          return 'Price:'
        case 'lar_pricing':
          return 'One Seller Price:'
        case 'dynamic_lead_pricing':
          return 'Dynamic Lead Price:'
      }
    },
    simplePricing () {
      return this.assignmentTermForm.pricingSetting === 'simple_pricing'
    },
    larPricing () {
      return this.assignmentTermForm.pricingSetting === 'lar_pricing'
    },
    dynamicLeadPricing () {
      return this.assignmentTermForm.pricingSetting === 'dynamic_lead_pricing'
    },
    persistedRecord () {
      return !!this.id
    },
    submitButtonText () {
      if (this.persistedRecord) {
        return 'SAVE'
      } else {
        return 'Create'
      }
    },
    radiusPresent () {
      return !!this.assignmentTermForm.radius
    },
    radiusValid () {
      return this.radiusPresent && this.assignmentTermForm.radius > 0
    },
    canBeSubmited () {
      if (this.persistedRecord) {
        return !this.$v.$invalid
      } else {
        return !this.$v.$invalid && this.districtCodesValid
      }
    },
    districtCodesFormDirty () {
      return (
        !!this.assignmentTermForm.districtCodesForm.countryCode ||
        this.assignmentTermForm.districtCodesForm.entireCountry ||
        !!this.assignmentTermForm.districtCodesForm.zipCodesList.length
      )
    },
    districtCodesFormValidOrEmpty () {
      if (this.assignmentTermForm.districtCodesForm.entireCountry) {
        return !!this.assignmentTermForm.districtCodesForm.countryCode
      } else {
        return (
          !!this.assignmentTermForm.districtCodesForm.countryCode &&
          !!this.assignmentTermForm.districtCodesForm.zipCodesList.length
        )
      }
    },
    districtCodesValid () {
      return this.districtCodesFormValidOrEmpty || this.radiusValid
    },
    dailyRemainDiff: {
      get () {
        return this.assignmentTermForm.contingentForm.dailyRemainDiff
      },
      set (diffAmount) {
        this.assignmentTermForm.contingentForm.dailyRemainDiff = diffAmount
        this.assignmentTermForm.contingentForm.dailyRemain =
          this.contingent.daily_remain + diffAmount
      }
    },
    weeklyRemainDiff: {
      get () {
        return this.assignmentTermForm.contingentForm.weeklyRemainDiff
      },
      set (diffAmount) {
        this.assignmentTermForm.contingentForm.weeklyRemainDiff = diffAmount
        this.assignmentTermForm.contingentForm.weeklyRemain =
          this.contingent.weekly_remain + diffAmount
      }
    },
    monthlyRemainDiff: {
      get () {
        return this.assignmentTermForm.contingentForm.monthlyRemainDiff
      },
      set (diffAmount) {
        this.assignmentTermForm.contingentForm.monthlyRemainDiff = diffAmount
        this.assignmentTermForm.contingentForm.monthlyRemain =
          this.contingent.monthly_remain + diffAmount
      }
    },
    uniqueRemainDiff: {
      get () {
        return this.assignmentTermForm.contingentForm.uniqueRemainDiff
      },
      set (diffAmount) {
        this.assignmentTermForm.contingentForm.uniqueRemainDiff = diffAmount
        this.assignmentTermForm.contingentForm.uniqueRemain =
          this.contingent.unique_remain + diffAmount
      }
    },
    buysDatesLabel () {
      return this.buysDatesOptions.find(option => {
        return option.value === this.buys_dates
      }).text
    },
    buysDatesTypeLabel () {
      var option = this.grossNetOptions.find(option => {
        return option.value === this.ordered_dates_type
      })
      return option ? option.text : 'UNKNOWN'
    },
    testAssignmentTerm () {
      return this.assignmentTermForm.test === true
    }
  },
  validations () {
    let validation = this.defaultValdationRules

    if (!this.dynamicLeadPricing) {
      validation = deepmerge(validation, this.oneSellerPriceRequired)
    }
    if (this.assignmentTermForm.buysDates && this.isNet) {
      validation = deepmerge(validation, this.feedback_time_validation)
    }
    if (this.simplePricing || this.dynamicLeadPricing) {
      return validation
    } else {
      validation = deepmerge(validation, this.dynamicPricesValidation)

      if (this.assignmentTermForm.threeSellersPrice) {
        validation = deepmerge(
          deepmerge(validation, this.twoSellersPriceRequired),
          this.oneSellerPriceRequired
        )
      }
      if (this.assignmentTermForm.twoSellersPrice) {
        validation = deepmerge(validation, this.oneSellerPriceRequired)
      }
      return validation
    }
  },
  methods: {
    submitForm (e) {
      this.errors = []

      if (!this.$v.$invalid) {
        if (this.assignmentTermForm.orderedDatesType === 'gross') {
          this.assignmentTermForm.feedbackTime = ''
        }

        if (this.persistedRecord) {
          const params = Object.assign(
            this.priceParams(),
            this.assignmentTermForm
          )
          return this.updateAssignmentTerm(this.id, params)
        }
      }
    },
    capitalized_ordered_dates_type () {
      return _.capitalize(this.ordered_dates_type)
    },
    isValidFeedBackTime () {
      let validator = this.$v.assignmentTermForm.feedbackTime

      if (!validator.required) {
        this.feedbackTimeError = 'Required field'
        return false
      }
      if (!validator.between) {
        this.feedbackTimeError = `Must be between ${
          validator.$params.between.min
        } and ${validator.$params.between.max}`
        return false
      }
      if (!validator.positiveInteger) {
        this.feedbackTimeError = 'Please use a positive integer'
        return false
      } else {
        return true
      }
    },
    isValidPricing (kindOfPrice) {
      let validator = this.$v.assignmentTermForm[kindOfPrice]
      let value = this.assignmentTermForm[kindOfPrice]

      if (
        validator &&
        !_.isUndefined(validator.required) &&
        !validator.required
      ) {
        this.priceErrorMessages[kindOfPrice] = 'Required field'
        return false
      }

      if (
        validator &&
        !_.isUndefined(validator.lessOrEqlFirstPrice) &&
        !validator.lessOrEqlFirstPrice
      ) {
        this.priceErrorMessages[kindOfPrice] =
          'Should be lower or equal to One Seller Price'
        return false
      }

      if (
        validator &&
        !_.isUndefined(validator.lessOrEqlSecondPrice) &&
        !validator.lessOrEqlSecondPrice
      ) {
        this.priceErrorMessages[kindOfPrice] =
          'Should be lower or equal to Two Sellers Price'
        return false
      }

      if (validator && !validator.integerOrFloat) {
        if (/^(\d+((,)|(,\d+))*)?$/.test(value)) {
          this.priceErrorMessages[kindOfPrice] = 'Please use . instead of ,'
          return false
        } else if (/^-/.test(value)) {
          this.priceErrorMessages[kindOfPrice] = 'Please use a positive number'
          return false
        } else {
          this.priceErrorMessages[kindOfPrice] = 'Please use a number'
          return false
        }
      } else {
        return true
      }
    },
    priceParams () {
      if (this.simplePricing) {
        let commonValue = this.assignmentTermForm.oneSellerPrice
        this.assignmentTermForm.twoSellersPrice = commonValue
        this.assignmentTermForm.threeSellersPrice = commonValue
      }
      if (this.dynamicLeadPricing) {
        let commonValue = this.assignmentTermForm.dynamicLeadPricingValue
        this.assignmentTermForm.oneSellerPrice = commonValue
        this.assignmentTermForm.twoSellersPrice = commonValue
        this.assignmentTermForm.threeSellersPrice = commonValue
      }
      return {
        oneSellerPrice: this.assignmentTermForm.oneSellerPrice,
        twoSellersPrice: this.assignmentTermForm.twoSellersPrice,
        threeSellersPrice: this.assignmentTermForm.threeSellersPrice
      }
    },
    removeUniqueRemain () {
      this.assignmentTermForm.contingentForm.uniqueRemainDiff = 'INFINITY'
      this.assignmentTermForm.contingentForm.uniqueRemain = null
      this.switchRemainDiffToDisplay('unique')
    },
    handleClickOnRequestedChangesTab () {
      window.open(
        this.linkForAssignmentTermInInternalAdmin('requested-changes'),
        '_blank'
      )
    },
    handleClickOnAuditsTab () {
      window.open(
        this.linkForAssignmentTermInInternalAdmin('audits'),
        '_blank'
      )
    },
    handleClickOnDigitalMatching () {
      window.open(
        this.linkForAssignmentTermInInternalAdmin('current-info'),
        '_blank'
      )
    },
    linkForAssignmentTermInInternalAdmin (tabName) {
      return `${process.vars.INTERNAL_ADMIN_URL}/assignment-terms/vendor/${
        this.vendorUuid
      }/product/${this.productUuid}?at_id=${this.id}&tab=${tabName || ''}`
    },
    updateAssignmentTerm (id, params) {
      return new Promise((resolve, reject) => {
        AssignmentTermsProvider.update(id, params).then(
          resp => {
            this.refreshFunction(resp.body.assignment_term)
            this.resetRemainDiffs()
            if (
              resp.body.assignment_term.pricing.setting ===
              'dynamic_lead_pricing'
            ) {
              this.setDynamicLeadPrice(resp.body.assignment_term.lead_price)
            }
            this.showSuccessUpdateMessage = true
            setTimeout(() => {
              this.showSuccessUpdateMessage = false
            }, 2000)
            resolve(resp)
          },
          resp => {
            this.errors = resp.body.errors
            resolve(resp)
          }
        )
      })
    },
    resetRemainDiffs () {
      this.assignmentTermForm.contingentForm.dailyRemainDiff = null
      this.assignmentTermForm.contingentForm.weeklyRemainDiff = null
      this.assignmentTermForm.contingentForm.monthlyRemainDiff = null
      this.assignmentTermForm.contingentForm.uniqueRemainDiff = null
    },
    setDynamicLeadPrice (leadPrice) {
      this.assignmentTermForm.dynamicLeadPricingValue = leadPrice
    },
    buildAssignmentTermForm () {
      return {
        vendorId: this.vendorId,
        productId: this.productId,
        name: this.name,
        radius: this.radius,
        deliveryStartAt: this.delivery_start_at,
        instantBuy: this.instant_buy,
        buysDates: this.buys_dates,
        orderedDatesType: this.ordered_dates_type,
        feedbackTime: this.feedback_time,
        status: this.status,
        pricingId: this.pricing.id,
        pricingSetting: this.pricing.setting,
        leadPrice: this.lead_price,
        oneSellerPrice: this.one_seller_price,
        twoSellersPrice: this.two_sellers_price,
        threeSellersPrice: this.three_sellers_price,
        dynamicLeadPricingValue: this.lead_price,
        autoAssignmentRanking: this.auto_assignment_ranking,
        test: this.test,
        contingentForm: {
          id: this.contingent.id,
          daily: this.contingent.daily,
          weekly: this.contingent.weekly,
          monthly: this.contingent.monthly,
          dailyRemain: this.contingent.daily_remain,
          weeklyRemain: this.contingent.weekly_remain,
          monthlyRemain: this.contingent.monthly_remain,
          uniqueRemain: this.contingent.unique_remain,
          dailyRemainDiff: null,
          weeklyRemainDiff: null,
          monthlyRemainDiff: null,
          uniqueRemainDiff: null
        },
        districtCodesForm: {
          countryCode: null,
          zipCodesList: [],
          entireCountry: false
        }
      }
    },
    switchRemainDiffToDisplay (kind) {
      this.contingentRemainsDisplay[kind] = !this.contingentRemainsDisplay[kind]
    },
    toggleNameEdit (state, id) {
      this.nameEditMode = state
      if (state) {
        this.$nextTick(() => {
          let input = this.$refs[`nameInput-${id}`]
          input.focus()
        })
      }
    },
    fetchAllPriceSettings () {
      var cachedPriceSettings = JSON.parse(
        localStorage.getItem('pricingSettings')
      )
      if (
        cachedPriceSettings === null ||
        cachedPriceSettings.expiry === undefined ||
        Date.now() > cachedPriceSettings.expiry
      ) {
        return new Promise((resolve, reject) => {
          PricingSettingsProvider.all().then(
            resp => {
              cachedPriceSettings = JSON.parse(resp.bodyText)
              cachedPriceSettings.expiry = Date.now() + 1000 * 60 * 60 * 12
              localStorage.setItem(
                'pricingSettings',
                JSON.stringify(cachedPriceSettings)
              )
              resolve(resp)
            },
            resp => {
              this.errors = resp.body.errors
              resolve(resp)
            }
          )
        })
      }
      this.pricingSettings = cachedPriceSettings.pricing_settings
    }
  },
  watch: {
    vendorId (vendorId) {
      this.assignmentTermForm.vendorId = vendorId
    },
    productId (productId) {
      this.assignmentTermForm.productId = productId
    }
  }
}
</script>

<style>
.btn-active-trigger span {
  color: darkslategray;
}
.btn-active-trigger.focus {
  box-shadow: none;
}
.btn.active {
  border-radius: 6px;
  border-width: 2px;
  border-style: solid;
  border-color: darkslategray;
}
.vue-js-switch#toggle-btn {
  font-size: 14px;
}
</style>
<style scoped>
.status {
  margin-left: 15px;
  font-weight: bold;
}

form {
  margin-top: 20px;
}

.assignment-term-heading {
  margin-top: 30px;
}
</style>
